import { useEffect, useRef, useState } from 'react';
import { trackingApi } from '@/api/endpoints/tracking';
import { useAuth } from '@/auth/AuthProvider';

const SCRIPT_ID = 'clarity-tag-script';
/** Та же маска, что у ClaritySetting на бэкенде. */
const PROJECT_ID_PATTERN = /^[a-z0-9]{6,20}$/;

type ClarityTag = ((...args: unknown[]) => void) & { q?: unknown[][] };

declare global {
  interface Window {
    clarity?: ClarityTag;
  }
}

function ensureClarityQueue(): ClarityTag {
  if (!window.clarity) {
    const queued: ClarityTag = (...args: unknown[]) => {
      (queued.q = queued.q || []).push(args);
    };
    window.clarity = queued;
  }
  return window.clarity;
}

/**
 * Microsoft Clarity (TRK-7): пишет всех, включая гостей, монтируется выше
 * auth-гейта. Project id решает сервер: залогиненному — из /me после ответа
 * в этой сессии (isAccessResolved), гостю — GET /clarity-config. В Clarity
 * уходит только identify(user.id); ad_Storage всегда denied.
 *
 * Метки для фильтров панели: `surface` = landing | app, `visitor_type` =
 * registered | guest — те же ключи и значения ставит лендинг, иначе фильтр
 * в панели распался бы надвое.
 */
export function ClarityTracking() {
  const { user, isAuthenticated, isAccessResolved } = useAuth();
  const [guestProjectId, setGuestProjectId] = useState<string | null>(null);
  const guestRequested = useRef(false);
  const identifiedUserId = useRef<number | null>(null);

  const userId = isAuthenticated ? (user?.id ?? null) : null;
  const rawProjectId = isAuthenticated
    ? isAccessResolved
      ? (user?.clarity?.project_id ?? null)
      : null
    : guestProjectId;
  const projectId = rawProjectId && PROJECT_ID_PATTERN.test(rawProjectId) ? rawProjectId : null;

  useEffect(() => {
    if (isAuthenticated || guestRequested.current) return;
    guestRequested.current = true;
    trackingApi
      .clarityConfig()
      .then((response) => setGuestProjectId(response?.clarity?.project_id ?? null))
      .catch(() => {});
  }, [isAuthenticated]);

  useEffect(() => {
    if (!projectId) return;
    const clarity = ensureClarityQueue();
    if (!document.getElementById(SCRIPT_ID)) {
      const s = document.createElement('script');
      s.id = SCRIPT_ID;
      s.src = `https://www.clarity.ms/tag/${projectId}`;
      s.async = true;
      document.head.appendChild(s);
    }
    // Порядок важен: сначала согласие, потом метки и identify. До загрузки тега вызовы копятся в очереди.
    clarity('consentv2', { analytics_Storage: 'granted', ad_Storage: 'denied' });
    clarity('set', 'surface', 'app');
    clarity('set', 'visitor_type', userId !== null ? 'registered' : 'guest');
    if (userId !== null && identifiedUserId.current !== userId) {
      clarity('identify', String(userId));
      identifiedUserId.current = userId;
    }
  }, [projectId, userId]);

  // Вход админа или внутреннего пользователя поверх гостевой записи: сервер отдал null → consent(false).
  useEffect(() => {
    if (!isAuthenticated || !isAccessResolved || projectId) return;
    if (!document.getElementById(SCRIPT_ID)) return;
    window.clarity?.('consent', false);
  }, [isAuthenticated, isAccessResolved, projectId]);

  return null;
}
